import { BoundToEdge } from '../../../component-classes';
import { getVentAlignedPassthroughX } from './get-vent-aligned-passthrough-x';
import { HorizontalAlignment } from '../../shared.enums';
import { Panel } from '../panel.class';
import { PanelItem } from '../../panel-items/panel-item.class';
import { ReferenceMap } from '../../shared.types';
import { Vertex3 } from '../../../spatial-classes';

export type AddPanelPassthroughParams = {
    panel: Panel;
    refMap: ReferenceMap;
};

export const addPanelPassthrough = (params: AddPanelPassthroughParams): number => {
	const { panel, refMap } = params;
	// Resolve the correct island for this panel (critical for target panels in cross-island connections).
	// Kitchen-scoped events (surround_material.updated) carry a refMap with no islands/island, so fall
	// back to the kitchen's own island collection or the add silently no-ops and passthroughs are lost.
	const island = (refMap.islands ?? refMap.kitchen?.design?.islands)?.findByPanelId(panel.id) ?? refMap.island;
	const { thickness } = panel;

	// determine passthrough SKU and geometry based on surround material
	const surroundKey = island?.surround?.key ?? '';
	const isMok = surroundKey.startsWith('moks/');
	const passthroughSku = isMok ? 'MOK-PASSTHROUGH' : 'PASSTHROUGH';
	const halfPassthroughWidth = isMok ? 1.75 : 2;
	let count = 0;
	let xPosition = 0;

	// SKU-based check since category.isPassthrough doesn't match cutout-type passthroughs
	const hasExistingPassthrough = panel.panelItemGroups.children.some((g) =>
		g.items.children.some((pi) => pi.item.sku === 'PASSTHROUGH' || pi.item.sku === 'MOK-PASSTHROUGH')
	);
	if (!hasExistingPassthrough && island?.connections?.children?.length > 0) {
		// Standard lookup: match source panel + overlap alignment
		const oIslandConnection = island.connections.children.find((con) => {
			return con.sourcePanelId === panel.id &&
				con.horizontalAlign === (island.isRightOverlap ? HorizontalAlignment.right : HorizontalAlignment.left);
		});

		// MOK lookup: find any connection where this panel is the source (without hAlign filter)
		const mokPanelConnection = isMok
			? island.connections.children.find((con) => con.sourcePanelId === panel.id)
			: null;

		let defaultX: number;
		if (isMok) {
			// MOK: 3" from side panel edge to center of cutout + adjoining front panel thickness
			defaultX = 3 + thickness;
		} else {
			const connectedPanelHasVents = oIslandConnection?.targetPanel.hasVents ?? panel.hasVents ?? false;
			defaultX = connectedPanelHasVents
				? 9 + halfPassthroughWidth
				: 6.5 + halfPassthroughWidth;
		}

		if (panel.isFrontPanel) {
			if (isMok && mokPanelConnection) {
				// MOK front panel: use connection hAlign to determine direction
				if (mokPanelConnection.horizontalAlign === HorizontalAlignment.left) {
					xPosition = defaultX + (panel.size.width / -2);
				} else {
					xPosition = -defaultX + (panel.size.width / 2);
				}
			} else if (island.isRightOverlap) {
				xPosition = -defaultX + (panel.size.width / 2);
			} else {
				xPosition = defaultX + (panel.size.width / -2);
			}
		} else {
			if (isMok && mokPanelConnection) {
				// MOK side/back panel: use connection hAlign to determine direction
				if (mokPanelConnection.horizontalAlign === HorizontalAlignment.left) {
					xPosition = (defaultX - thickness) - (panel.size.width / 2);
				} else {
					xPosition = -(defaultX - thickness) + (panel.size.width / 2);
				}
			} else if (isMok) {
				// MOK fallback: use panel side heuristic
				const towardsConnection = panel.isRightPanel !== island.isRightOverlap;
				if (towardsConnection) {
					xPosition = (defaultX - thickness) - (panel.size.width / 2);
				} else {
					xPosition = -(defaultX - thickness) + (panel.size.width / 2);
				}
			} else {
				const ventAlignedX = panel.hasVents
					? getVentAlignedPassthroughX({ panel, alignRight: !island.isRightOverlap, halfPassthroughWidth })
					: null;
				if (ventAlignedX !== null) {
					xPosition = ventAlignedX;
				} else if (island.isRightOverlap) {
					xPosition = (defaultX - thickness) - (panel.size.width / 2);
				} else {
					xPosition = -(defaultX - thickness) + (panel.size.width / 2);
				}
			}
		}

		count++;
		const piGroup = panel.panelItemGroups.new();
		const passthrough = new PanelItem({ sku: passthroughSku, parent: piGroup });
		passthrough.boundToEdge = BoundToEdge.Bottom;

		if (isMok) {
			// MOK passthrough sits 3" up from the bottom edge. Use offset.y, not center.y:
			// getItemCenterCfn recomputes center.y for a boundBottom item and only preserves
			// offset.y, so a direct center.y write is discarded on the next read (GitHub #305).
			passthrough.offset.y = -3;
		}

		piGroup.center = new Vertex3({ dataIn: { x: xPosition } });
		piGroup.items.children.push(passthrough);
	}

	return count;
};
